import {
  Component,
  Input,
  forwardRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  SimpleChanges,
  OnChanges,
  HostBinding,
  OnInit,
  DestroyRef,
  inject,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Query } from 'app/hub-components/models/analytics-api/query.model';
import { DatesService } from 'app/hub-components/modules/date-range-picker/dates.service';
import { DateRangeValue, DynamicDateRange, FixedDateRange } from '../../modules/date-range-picker/types';
import { BenchmarkService } from '../../services/benchmark/benchmark.service';

export const VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BenchmarksComponent),
  multi: true,
};

export interface BenchmarkPeriodOption { name: string; value: string }
export interface BenchmarkSelection extends BenchmarkPeriodOption {
  dateRange?: DateRangeValue;
  query?: {} | Query;
}

const DEFAULT_PERIOD_OPTION: BenchmarkPeriodOption = { name: 'None', value: 'none' };
const DEFAULT_DATE_RANGE_PERIOD: string = 'previous_period';

@Component({
  selector: 'benchmarks',
  styleUrls: ['benchmarks.component.less'],
  templateUrl: 'benchmarks.component.html',
  providers: [VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BenchmarksComponent implements ControlValueAccessor, OnChanges, OnInit {
  @HostBinding('class') hostClass = 'tw-inline-block tw-w-full';

  @Input() options: BenchmarkPeriodOption[];
  @Input() dates: DynamicDateRange;
  @Input() queryFirstDayOfWeek: string = undefined;
  @Input() datePickerPosition: 'right' | 'bottom' = 'right';

  public dateRange: string;
  public periodControl = new FormControl<BenchmarkPeriodOption>(DEFAULT_PERIOD_OPTION);
  public datePickerControl = new FormControl<DateRangeValue | null>(null);

  private readonly destroyRef = inject(DestroyRef);
  private onChangeFn: ((value: BenchmarkSelection | null) => void) | null = null;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private benchmarkService: BenchmarkService,
    private datesService: DatesService,
  ) {}

  public ngOnInit(): void {
    this.subscribeToPeriodChanges();
    this.subscribeToDatePickerChanges();
  }

  private subscribeToDatePickerChanges(): void {
    this.datePickerControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dateRangeValue: DateRangeValue) => {
        if (!this.onChangeFn) {
          return;
        }

        const currentPeriod = this.periodControl.value || DEFAULT_PERIOD_OPTION;
        const baseSelection: BenchmarkSelection = { ...currentPeriod };

        if (!dateRangeValue) {
          this.onChangeFn(baseSelection);
          return;
        }

        this.onChangeFn({
          ...baseSelection,
          dateRange: dateRangeValue,
        });
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['dates']) {
      this.setDateRange();
    }
  }

  public writeValue(modelValue: BenchmarkSelection | null): void {
    const selection = modelValue || null;

    let period: BenchmarkPeriodOption = DEFAULT_PERIOD_OPTION;

    if (selection) {
      const matchedOption = this.options?.find(option => option.value === selection.value);
      period = matchedOption ?? { name: selection.name, value: selection.value };
    }

    this.periodControl.setValue(period, { emitEvent: false });

    if (selection?.value === 'custom' && selection.dateRange) {
      this.datePickerControl.setValue(selection.dateRange, { emitEvent: false });
    } else {
      this.datePickerControl.setValue(null, { emitEvent: false });
    }

    this.setDateRange();
    this.changeDetectorRef.markForCheck();
  }

  public registerOnChange(fn: (value: BenchmarkSelection | null) => void): void {
    this.onChangeFn = fn;
  }

  public registerOnTouched(_fn: any): void {}

  private subscribeToPeriodChanges(): void {
    this.periodControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: BenchmarkPeriodOption) => {
        if (!value) {
          if (this.onChangeFn) {
            this.onChangeFn(null);
          }
          return;
        }

        let selection: BenchmarkSelection = { ...value };

        if (value.value === 'custom' && this.dates) {
          const defaultDateRange = this.getDatePickerDefaultValue();
          selection = {
            ...value,
            dateRange: defaultDateRange,
          };
          this.datePickerControl.setValue(defaultDateRange, { emitEvent: false });
        } else {
          this.datePickerControl.setValue(null, { emitEvent: false });
        }

        this.setDateRange();
        this.changeDetectorRef.markForCheck();

        if (this.onChangeFn) {
          this.onChangeFn(selection);
        }
      });
  }

  private setDateRange(): void {
    const option = this.periodControl?.value;
    if (!option?.value || option.value === 'none' || option.value === 'custom') {
      this.dateRange = '';
    } else if (this.dates) {
      const benchmarks = this.benchmarkService.calculateBenchmark(
        this.dates,
        option.value,
        undefined,
        this.queryFirstDayOfWeek ? Number(this.queryFirstDayOfWeek) : undefined,
      );
      if (benchmarks?.startDate && benchmarks?.endDate) {
        const fixedDateRange = this.datesService.dateRangeToFixedDateRange({
          startDate: benchmarks.startDate,
          endDate: benchmarks.endDate,
        });
        this.dateRange = this.datesService.intervalDateRange(
          fixedDateRange,
          this.queryFirstDayOfWeek ? Number(this.queryFirstDayOfWeek) : undefined,
        );
      } else {
        this.dateRange = '';
      }
    } else {
      this.dateRange = '';
    }
    this.changeDetectorRef.markForCheck();
  }

  private getDatePickerDefaultValue(): FixedDateRange {
    const defaultPeriod = this.options.find(option => option.value === DEFAULT_DATE_RANGE_PERIOD);
    return this.benchmarkService.getCustomPeriodDefaultDateRange(this.dates, defaultPeriod, this.queryFirstDayOfWeek ? Number(this.queryFirstDayOfWeek) : undefined);
  }
}
